"use client";

import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from "react";

import type { Conversation } from "@casey/db-types";
import { createVoiceChatApiClient } from "@workspace/voice-chat-ui";
import type { TokenRefreshContext } from "@workspace/voice-chat-ui";

// ============================================================================
// API Client Singleton
// ============================================================================

/**
 * Singleton API client for voice chat.
 * Created once at module load time with the API URL from environment.
 * Token refresh callback is set up when InviteFlowProvider mounts.
 */
const voiceChatApiClient = createVoiceChatApiClient({
  apiUrl: process.env.NEXT_PUBLIC_API_URL,
});

// ============================================================================
// Types
// ============================================================================

export type BackendFlavor = "legacy" | "flow" | "elevenlabs" | "livekit";

/**
 * Capabilities that vary by backend flavor.
 * This allows UI components to adapt based on what the transport supports.
 */
export interface FlavorCapabilities {
  /** Whether the transport supports resuming a disconnected session */
  supportsResume: boolean;
  /** Whether previous sessions can be listed and resumed */
  supportsSessionListing: boolean;
}

/**
 * Get capabilities for a given backend flavor.
 */
export function getFlavorCapabilities(
  flavor: BackendFlavor | undefined,
): FlavorCapabilities {
  switch (flavor) {
    case "elevenlabs":
      return {
        supportsResume: false,
        supportsSessionListing: false,
      };
    case "livekit":
      return {
        supportsResume: false,
        supportsSessionListing: false,
      };
    case "legacy":
    case "flow":
    default:
      return {
        supportsResume: true,
        supportsSessionListing: true,
      };
  }
}

/**
 * InviteFlowData - Connection and organization context
 *
 * This stores data needed to:
 * 1. Connect to a conversation (auth, connection code)
 * 2. Display organization branding (name, avatar, greeting)
 * 3. Pass context to the conversation (sessionContext)
 *
 * Conversation-specific data (id, status, transport credentials)
 * is stored in ConversationContext instead.
 */
export interface InviteFlowData {
  /** Connection code for the agent */
  connectionCode: string | null;
  /** Greeting title to display */
  greetingTitle: string | null;
  /** Greeting body to display */
  greetingBody: string | null;
  /** Organization name for branding */
  organizationName: string | null;
  /** Organization avatar URL for branding */
  organizationAvatarUrl: string | null;
  /** Context to pass to the agent (organization info, participant profile, etc.) */
  sessionContext: Record<string, unknown> | null;
  /** Backend flavor for the connection */
  flavor?: BackendFlavor;
  /** Ephemeral token for client API calls (pre-fetched on invite page) */
  ephemeralToken?: string;
  /** Token expiration timestamp */
  tokenExpiresAt?: string;
  /**
   * Session data for the newly created conversation.
   * This is passed to ConversationContext on the live page.
   * @deprecated Prefer setting conversation data via ConversationContext directly
   */
  sessionData?: Partial<Conversation>;
  /** Daily room URL - passed to ConversationContext */
  dailyRoom?: string;
  /** Daily token - passed to ConversationContext */
  dailyToken?: string;
  /** ElevenLabs token - passed to ConversationContext */
  elToken?: string;
  /** LiveKit server URL - passed to ConversationContext */
  lkServerUrl?: string;
  /** LiveKit participant token - passed to ConversationContext */
  lkToken?: string;
}

// ============================================================================
// Context
// ============================================================================

interface InviteFlowContextType {
  /** Current invite flow data (connection, org, auth) */
  inviteFlowData: InviteFlowData | null;
  /** Set or update invite flow data */
  setInviteFlowData: (
    data:
      | InviteFlowData
      | null
      | ((prev: InviteFlowData | null) => InviteFlowData | null),
  ) => void;
  /** Clear all invite flow data */
  clearInviteFlowData: () => void;
  /** Singleton API client for voice chat operations */
  apiClient: ReturnType<typeof createVoiceChatApiClient>;
}

const InviteFlowContext = createContext<InviteFlowContextType | undefined>(
  undefined,
);

// ============================================================================
// Provider
// ============================================================================

export function InviteFlowProvider({ children }: { children: ReactNode }) {
  const [inviteFlowData, setInviteFlowData] = useState<InviteFlowData | null>(
    null,
  );
  // Use ref to access latest inviteFlowData in the callback without re-setting it
  const inviteFlowDataRef = useRef(inviteFlowData);
  inviteFlowDataRef.current = inviteFlowData;

  const clearInviteFlowData = () => {
    setInviteFlowData(null);
    // Clear the token from the API client when invite flow is cleared
    voiceChatApiClient.clearToken();
  };

  /**
   * Token refresh callback for the API client.
   * Called automatically when the client needs a token (no token, or 401 response).
   * Deduplication is handled by the client itself.
   */
  const refreshToken = useCallback(
    async (context: TokenRefreshContext): Promise<string> => {
      // Check if we already have a valid token in state
      const existingToken = inviteFlowDataRef.current?.ephemeralToken;
      if (existingToken) {
        // Token exists but client is asking for refresh (401 case)
        // Clear it so we fetch a new one
      }

      // Fetch a new ephemeral token
      const response = await fetch("/api/auth/ephemeral-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          connection_code: context.connectionCode,
          conversation_id: context.conversationId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch ephemeral token");
      }

      const data = await response.json();
      const token = data.token;

      if (!token) {
        throw new Error("No token received from auth endpoint");
      }

      // Store the token in inviteFlowData for persistence
      setInviteFlowData((prev) => ({
        ...prev,
        connectionCode: prev?.connectionCode ?? context.connectionCode ?? null,
        greetingTitle: prev?.greetingTitle ?? null,
        greetingBody: prev?.greetingBody ?? null,
        organizationName: prev?.organizationName ?? null,
        organizationAvatarUrl: prev?.organizationAvatarUrl ?? null,
        sessionContext: prev?.sessionContext ?? null,
        ephemeralToken: token,
        tokenExpiresAt: data.expiresAt,
      }));

      return token;
    },
    [],
  );

  // Set up token refresh callback on mount
  useEffect(() => {
    voiceChatApiClient.setTokenRefreshCallback(refreshToken);
  }, [refreshToken]);

  // Sync ephemeral token to API client whenever inviteFlowData changes
  // (handles case where token is set externally, e.g., from storage)
  useEffect(() => {
    const token = inviteFlowData?.ephemeralToken;
    if (token && voiceChatApiClient.getToken() !== token) {
      voiceChatApiClient.setToken(token);
    }
  }, [inviteFlowData?.ephemeralToken]);

  const value = useMemo(
    () => ({
      inviteFlowData,
      setInviteFlowData,
      clearInviteFlowData,
      apiClient: voiceChatApiClient,
    }),
    [inviteFlowData],
  );

  return (
    <InviteFlowContext.Provider value={value}>
      {children}
    </InviteFlowContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access invite flow context (connection, org, auth).
 */
export function useInviteFlow() {
  const context = useContext(InviteFlowContext);
  if (context === undefined) {
    throw new Error("useInviteFlow must be used within InviteFlowProvider");
  }
  return context;
}

/**
 * Hook to access the singleton voice chat API client.
 * This is the preferred way to access the API client throughout the app.
 */
export function useVoiceChatApiClient() {
  const context = useContext(InviteFlowContext);
  if (context === undefined) {
    throw new Error(
      "useVoiceChatApiClient must be used within InviteFlowProvider",
    );
  }
  return context.apiClient;
}
